import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Text,
  Checkbox,
  VStack,
  useToast,
  useColorModeValue,
  Icon,
  Heading
} from '@chakra-ui/react';
import { FaChartLine } from 'react-icons/fa';
import axios from 'axios';

const LnaMonthlyModal = ({ isOpen, onClose, onComplete }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const bgModal = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");

  useEffect(() => {
    if (isOpen) {
      // Fetch all unique categories from the backend to list them
      const fetchCategories = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
          setCategories(res.data.map(c => c.name));
        } catch (error) {
          console.error("Failed to fetch categories", error);
        }
      };
      fetchCategories();
    }
  }, [isOpen]);

  const handleToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('user'))?.token || "";
      await axios.post(`${process.env.REACT_APP_API_URL}/api/lna/monthly`, {
        difficultCategories: selectedCategories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Feedback Saved",
        description: selectedCategories.length > 0 ? "Your training needs have been updated." : "Great job staying on top of your training!",
        status: "success",
        duration: 3000,
      });
      onClose();
      if (onComplete) onComplete();
    } catch (err) {
      toast({
        title: "Error saving feedback",
        status: "error",
        duration: 3000,
      });
    }
    setLoading(false);
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(5px)" bg="blackAlpha.600" />
      <ModalContent bg={bgModal} borderRadius="2xl" p={4} boxShadow="2xl">
        <ModalHeader textAlign="center">
          <Icon as={FaChartLine} w={10} h={10} color="purple.500" mb={4} />
          <Heading size="md" mb={2}>Monthly Learning Check-in</Heading>
          <Text fontSize="sm" color={textColor} fontWeight="normal">
            To help us personalize your learning journey, please select any topics you found particularly challenging or difficult this past month.
          </Text>
        </ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={3} maxH="200px" overflowY="auto" px={2}>
            {categories.length > 0 ? categories.map((cat, idx) => (
              <Checkbox 
                key={idx} 
                colorScheme="purple"
                isChecked={selectedCategories.includes(cat)}
                onChange={() => handleToggle(cat)}
                size="lg"
              >
                <Text fontSize="sm" fontWeight="600">{cat}</Text>
              </Checkbox>
            )) : (
              <Text fontSize="sm" color="gray.500">Loading topics...</Text>
            )}
            <Checkbox 
                colorScheme="purple"
                isChecked={selectedCategories.length === 0}
                onChange={() => setSelectedCategories([])}
                size="lg"
                mt={2}
              >
                <Text fontSize="sm" fontWeight="600" color="green.500">None, I'm doing great!</Text>
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter justify="center" mt={4}>
          <Button 
            w="full"
            bgGradient="linear(to-r, purple.500, blue.500)"
            color="white"
            borderRadius="xl"
            isLoading={loading}
            onClick={handleSubmit}
            _hover={{ bgGradient: "linear(to-r, purple.600, blue.600)" }}
          >
            Submit Feedback
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LnaMonthlyModal;
